import { defineCollection } from "@rebasepro/common";
import prospectsCollection from "./prospects.js";
import companiesCollection from "./companies.js";
import emailThreadsCollection from "./email_threads.js";

/**
 * The timeline. One row per thing that happened to a person.
 *
 * ── Why a CRM needs this and a pipeline does not ─────────────────────────────
 * Every other collection here stores a *current state*: this prospect is
 * `ready`, this email is `sent`. State is what you need to decide the next
 * action, and it is exactly the wrong thing for answering "what happened with
 * this person?" — because each write destroys the previous answer. A card that
 * reads `replied` cannot tell you whether that took one email or three, how
 * long they took to answer, or that they bounced once and were re-found from a
 * different source a month later.
 *
 * So this collection is append-only by convention: rows are written, never
 * updated. It is the difference between a pipeline and a CRM.
 *
 * Borrowed from edith-crm's `activities`, minus its `collectionId` — that one
 * is polymorphic because Edith's objects are user-defined at runtime, and here
 * everything worth a timeline hangs off a prospect.
 */
const activitiesCollection = defineCollection({
    name: "Activity",
    singularName: "Activity",
    slug: "activities",
    table: "activities",
    description: "Append-only history of what happened to each person. Never edited — a corrected row would be a rewritten past.",
    properties: {
        id: { name: "ID", type: "string", isId: "uuid", admin: { readOnly: true } },

        prospect: {
            name: "Person",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => prospectsCollection,
                relationName: "prospect",
                localKey: "prospect_id",
                onDelete: "cascade"
            }
        },
        company: {
            name: "Company",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => companiesCollection,
                relationName: "company",
                localKey: "company_id",
                onDelete: "cascade"
            },
            description: "Set when it happened with the company rather than with one person in it — or with both"
        },
        thread: {
            name: "Conversation",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => emailThreadsCollection,
                relationName: "thread",
                localKey: "thread_id",
                onDelete: "set null"
            },
            description: "The conversation it happened in, so the timeline can open it"
        },

        kind: {
            name: "What happened",
            type: "string",
            validation: { required: true },
            enum: [
                { id: "lead_found", label: "Lead found", color: "gray" },
                { id: "prospect_found", label: "Person identified", color: "blue" },
                { id: "email_drafted", label: "Email drafted", color: "purple" },
                { id: "email_sent", label: "Email sent", color: "green" },
                { id: "reply_received", label: "Replied", color: "teal" },
                { id: "bounced", label: "Bounced", color: "red" },
                { id: "opted_out", label: "Opted out", color: "red" },
                { id: "status_changed", label: "Status changed", color: "yellow" },
                { id: "note", label: "Note", color: "gray" },
                { id: "call", label: "Call", color: "blue" },
                { id: "meeting", label: "Meeting", color: "purple" }
            ]
        },
        summary: {
            name: "Summary",
            type: "string",
            validation: { required: true },
            description: "One line, readable on its own — a timeline you have to click through is not a timeline"
        },
        detail: { name: "Detail", type: "string", admin: { multiline: true } },

        actor: {
            name: "By",
            type: "string",
            defaultValue: "agent",
            enum: [
                { id: "agent", label: "Agent", color: "blue" },
                { id: "human", label: "Human", color: "green" },
                { id: "prospect", label: "The person", color: "teal" }
            ],
            description: "Who caused it. The agent/human split is the audit trail for the two approval gates."
        },

        occurred_at: { name: "When", type: "date", autoValue: "on_create", admin: { readOnly: true } }
    },
    admin: {
        icon: "History",
        group: "Pipeline",
        defaultViewMode: "table",
        display: {
            title: "summary"
        },
        sort: ["occurred_at", "desc"],
        filterPresets: [
            { label: "Replies", filterValues: { kind: ["==", "reply_received"] } },
            { label: "By a human", filterValues: { actor: ["==", "human"] } }
        ],
        propertiesOrder: ["occurred_at", "kind", "summary", "prospect", "company", "thread", "actor", "detail"]
    }
});

export default activitiesCollection;
